<template>
  <div class="endpoint-manager">
    <div class="toolbar">
      <div>
        <div class="title">TikHub 接口档案</div>
        <div class="description">用于维护 TikHub 接口和项目接入状态。TikHub 没有接口与项目尚未开发会分别标明。</div>
      </div>
      <el-button type="primary" icon="el-icon-plus" :disabled="loading || saving || Boolean(deletingId)" @click="openCreate">新增接口</el-button>
    </div>

    <el-alert
      title="没有数据库脚本执行权限时，可直接在这里新增或修正接口元数据。"
      type="info"
      :closable="false"
      show-icon
      class="notice"
    />

    <el-table v-loading="loading" :data="items" empty-text="暂无接口档案，请点击“新增接口”录入">
      <el-table-column prop="platform" label="平台" min-width="110"/>
      <el-table-column prop="purpose" label="用途" min-width="130"/>
      <el-table-column label="类型" min-width="120">
        <template slot-scope="scope">{{ typeLabel(scope.row.entry_type) }}</template>
      </el-table-column>
      <el-table-column prop="method" label="方法" width="76"/>
      <el-table-column prop="path" label="接口路径" min-width="320" show-overflow-tooltip/>
      <el-table-column label="支持状态" width="180">
        <template slot-scope="scope">
          <el-tag :type="statusType(scope.row)" size="small">
            {{ statusLabel(scope.row) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="140" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" :disabled="Boolean(deletingId)" @click="openEdit(scope.row)">编辑</el-button>
          <el-button
            type="text"
            class="danger"
            :loading="deletingId === scope.row.id"
            :disabled="Boolean(deletingId) && deletingId !== scope.row.id"
            @click="remove(scope.row)"
          >{{ deletingId === scope.row.id ? '删除中' : '删除' }}</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :title="editingId ? '编辑接口档案' : '新增接口档案'"
      :visible.sync="dialogVisible"
      width="640px"
      :close-on-click-modal="false"
      :close-on-press-escape="!saving"
      :show-close="!saving"
      :before-close="beforeDialogClose"
      @closed="reset"
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="112px" :disabled="saving">
        <el-form-item label="平台" prop="platform">
          <el-select v-model="form.platform" filterable allow-create default-first-option placeholder="选择或输入平台标识" style="width:100%">
            <el-option v-for="platform in platformOptions" :key="platform" :label="platform" :value="platform"/>
          </el-select>
        </el-form-item>
        <el-form-item label="用途" prop="purpose">
          <el-input v-model.trim="form.purpose" maxlength="100" show-word-limit placeholder="例如：关键词搜索、视频详情、评论采集"/>
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="接口类型" prop="entry_type">
              <el-select v-model="form.entry_type" style="width:100%">
                <el-option label="关键词搜索" value="keyword_search"/>
                <el-option label="内容详情" value="detail"/>
                <el-option label="评论采集" value="comment"/>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="请求方法" prop="method">
              <el-radio-group v-model="form.method">
                <el-radio-button label="GET"/>
                <el-radio-button label="POST"/>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="接口路径" prop="path">
          <el-input v-model.trim="form.path" :disabled="form.path_status === 'TikHub不支持'" :placeholder="form.path_status === 'TikHub不支持' ? 'TikHub 无此接口，不需要填写' : '/api/v1/...'"/>
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="TikHub接口" prop="path_status">
              <el-select v-model="form.path_status" style="width:100%" @change="changePathStatus">
                <el-option label="待确认" value="待确认"/>
                <el-option label="待 Swagger 确认" value="待 Swagger 确认"/>
                <el-option label="TikHub 已支持" value="已确认"/>
                <el-option label="TikHub 不支持" value="TikHub不支持"/>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="需要实测">
              <el-switch v-model="form.needs_verification"/>
            </el-form-item>
          </el-col>
        </el-row>

        <el-collapse v-model="advanced">
          <el-collapse-item title="高级设置（成本、调用上限和文档）" name="advanced">
            <el-row :gutter="16">
              <el-col :span="12">
                <el-form-item label="单次成本" prop="unit_cost_usd">
                  <el-input-number v-model="form.unit_cost_usd" :min="0" :precision="6" :step="0.001" controls-position="right" style="width:100%"/>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="任务请求上限" prop="per_job_request_limit">
                  <el-input-number v-model="form.per_job_request_limit" :min="1" :max="500" controls-position="right" style="width:100%"/>
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="16">
              <el-col :span="12">
                <el-form-item label="成本等级">
                  <el-select v-model="form.cost_level" style="width:100%">
                    <el-option label="P0" value="P0"/>
                    <el-option label="P1" value="P1"/>
                    <el-option label="P2" value="P2"/>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="价格状态">
                  <el-select v-model="form.pricing_status" style="width:100%">
                    <el-option label="待确认" value="待确认"/>
                    <el-option label="已确认" value="已确认"/>
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>
            <el-form-item label="文档地址">
              <el-input v-model.trim="form.source_url" placeholder="TikHub 文档链接"/>
            </el-form-item>
            <el-form-item label="价格说明">
              <el-input v-model.trim="form.pricing_note" type="textarea" :rows="2" maxlength="500" show-word-limit/>
            </el-form-item>
          </el-collapse-item>
        </el-collapse>
      </el-form>
      <span slot="footer">
        <el-button :disabled="saving" @click="closeDialog">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">{{ saving ? '保存中' : '保存' }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { yuqingApi } from '@/api/yuqing'

const emptyForm = () => ({
  platform: '', purpose: '', entry_type: 'keyword_search', method: 'GET', path: '',
  path_status: '待确认', needs_verification: true, unit_cost_usd: 0,
  cost_level: 'P1', per_job_request_limit: 20, pricing_status: '待确认',
  pricing_note: '', source_url: ''
})

export default {
  data () {
    const apiPath = (rule, value, callback) => {
      if (this.form.path_status === 'TikHub不支持') return callback()
      if (!value) return callback(new Error('请输入接口路径'))
      if (!value.startsWith('/api/v1/')) return callback(new Error('接口路径必须以 /api/v1/ 开头'))
      callback()
    }
    return {
      items: [], loading: false, saving: false, deletingId: null, dialogVisible: false,
      editingId: null, advanced: [], form: emptyForm(),
      rules: {
        platform: [{ required: true, message: '请选择或输入平台', trigger: 'change' }],
        purpose: [{ required: true, message: '请输入接口用途', trigger: 'blur' }],
        entry_type: [{ required: true, message: '请选择接口类型', trigger: 'change' }],
        method: [{ required: true, message: '请选择请求方法', trigger: 'change' }],
        path: [{ required: true, validator: apiPath, trigger: 'blur' }]
      }
    }
  },
  computed: {
    platformOptions () {
      return Array.from(new Set(this.items.map(item => item.platform).filter(Boolean))).sort()
    }
  },
  mounted () { this.load() },
  methods: {
    async load () {
      this.loading = true
      try {
        this.items = (await yuqingApi.get('endpoints')).endpoints || []
      } catch (error) {
        this.items = []
        this.$message.error(error.message || '接口档案加载失败')
      } finally {
        this.loading = false
      }
    },
    typeLabel (value) {
      return ({ keyword_search: '关键词搜索', detail: '内容详情', comment: '评论采集' })[value] || value || '-'
    },
    statusLabel (item) {
      return ({
        tikhub_unsupported: 'TikHub 不支持',
        project_unadapted: '项目未适配，需开发',
        adapted: '项目已适配',
        pending_confirmation: 'TikHub 接口待确认'
      })[item.integration_status] || (item.path_status === 'TikHub不支持' ? 'TikHub 不支持' : item.path_status === '已确认' ? 'TikHub 已支持' : 'TikHub 接口待确认')
    },
    statusType (item) {
      return ({ adapted: 'success', tikhub_unsupported: 'info', project_unadapted: 'danger' })[item.integration_status] || (item.path_status === '已确认' ? 'success' : 'warning')
    },
    changePathStatus (value) {
      if (value === 'TikHub不支持') this.form.path = ''
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate('path'))
    },
    openCreate () {
      this.editingId = null
      this.form = emptyForm()
      this.advanced = []
      this.dialogVisible = true
    },
    openEdit (item) {
      this.editingId = item.id
      this.form = { ...emptyForm(), ...item }
      this.advanced = []
      this.dialogVisible = true
    },
    reset () {
      this.editingId = null
      this.form = emptyForm()
      this.advanced = []
      if (this.$refs.form) this.$refs.form.clearValidate()
    },
    closeDialog () {
      if (!this.saving) this.dialogVisible = false
    },
    beforeDialogClose (done) {
      if (!this.saving) done()
    },
    save () {
      this.$refs.form.validate(async valid => {
        if (!valid || this.saving) return
        this.saving = true
        try {
          if (this.editingId) await yuqingApi.put(`endpoints/${this.editingId}`, this.form)
          else await yuqingApi.post('endpoints', this.form)
          this.$message.success(this.editingId ? '接口档案已更新' : '接口档案已新增')
          this.dialogVisible = false
          await this.load()
          this.$emit('changed')
        } catch (error) {
          this.$message.error(error.message || '接口档案保存失败')
        } finally {
          this.saving = false
        }
      })
    },
    async remove (item) {
      if (this.deletingId) return
      try {
        await this.$confirm(`确定删除“${item.purpose}”接口档案吗？删除后无法恢复。`, '删除接口档案', {
          confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning'
        })
        this.deletingId = item.id
        await yuqingApi.delete(`endpoints/${item.id}`)
        this.$message.success('接口档案已删除')
        await this.load()
        this.$emit('changed')
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error(error.message || '接口档案删除失败')
      } finally {
        this.deletingId = null
      }
    }
  }
}
</script>

<style scoped>
.toolbar { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 16px; }
.title { color: #303133; font-size: 16px; font-weight: 600; line-height: 24px; }
.description { max-width: 760px; margin-top: 4px; color: #909399; font-size: 13px; line-height: 20px; }
.notice { margin-bottom: 16px; }
.danger { color: #f56c6c; }
</style>
